import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Check } from 'lucide-react';

interface StoryFramework {
  name: string;
  acronym: string;
  description: string;
  structure: string;
  bestFor: string;
  example: string;
}

const frameworks: StoryFramework[] = [
  {
    name: 'Problem-Agitate-Solution',
    acronym: 'PAS',
    description: 'Start with a problem, intensify the pain, then present your solution',
    structure: '1) State the Problem → 2) Agitate the consequences → 3) Offer the Solution',
    bestFor: 'Ads, blogs, thought leadership',
    example: 'Problem: "You\'re losing customers to slow checkout." Agitate: "Every second of friction costs you money." Solution: "Our one-click checkout recovers lost sales."'
  },
  {
    name: 'Attention-Interest-Desire-Action',
    acronym: 'AIDA',
    description: 'Hook attention, build interest, create desire, prompt action',
    structure: '1) Grab Attention → 2) Build Interest → 3) Create Desire → 4) Call to Action',
    bestFor: 'Social media, conversion-focused posts',
    example: 'Attention: Bold stat or question. Interest: Explain why it matters. Desire: Paint the transformation. Action: "Start today."'
  },
  {
    name: 'Before-After-Bridge',
    acronym: 'BAB',
    description: 'Show the current state, ideal state, and how to bridge the gap',
    structure: '1) Before (current pain) → 2) After (desired outcome) → 3) Bridge (how to get there)',
    bestFor: 'Short-form storytelling, case studies',
    example: 'Before: "Spending 10 hours/week on reports." After: "Automated in minutes." Bridge: "Here\'s how we did it."'
  },
  {
    name: 'Star-Story-Solution',
    acronym: 'SSS',
    description: 'Feature a character (star), tell their story, reveal the solution',
    structure: '1) Star (relatable character) → 2) Story (their journey) → 3) Solution (what helped)',
    bestFor: 'Email, testimonials, case studies',
    example: 'Star: Meet Sarah, a busy founder. Story: She struggled with X. Solution: This tool changed everything.'
  },
  {
    name: 'Hero\'s Journey (Compressed)',
    acronym: 'Hero',
    description: 'The classic monomyth condensed: ordinary world, challenge, transformation',
    structure: '1) Ordinary World → 2) Call to Adventure → 3) Transformation → 4) Return Changed',
    bestFor: 'Brand narratives, landing pages',
    example: 'Started as a struggling startup → Faced massive challenge → Found breakthrough → Now thriving and helping others.'
  },
  {
    name: 'Big Idea / Tension-Revelation-Shift',
    acronym: 'TRS',
    description: 'Present a provocative idea, build tension, reveal insight, shift perspective',
    structure: '1) Tension (challenging assumption) → 2) Revelation (new insight) → 3) Shift (changed perspective)',
    bestFor: 'Trend pieces, opinion-driven content',
    example: 'Tension: "Everyone says X." Revelation: "But data shows Y." Shift: "Here\'s what this means for you."'
  }
];

interface StoryFrameworkDialogProps {
  onSelect: (framework: string) => void;
  currentInstructions?: string;
  /** Control dialog open state externally */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional children to use as trigger instead of default button */
  children?: React.ReactNode;
}

export function StoryFrameworkDialog({
  onSelect,
  currentInstructions,
  open: externalOpen,
  onOpenChange,
  children
}: StoryFrameworkDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOpen !== undefined && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const handleSelect = (framework: StoryFramework) => {
    const instruction = `Use the ${framework.acronym} framework (${framework.name}): ${framework.structure}`;

    if (currentInstructions) {
      onSelect(`${currentInstructions}\n\n${instruction}`);
    } else {
      onSelect(instruction);
    }

    // Close dialog after selection
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children !== undefined ? (
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" type="button">
            <BookOpen className="h-4 w-4 mr-2" />
            Use a Story Framework
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Story Frameworks</DialogTitle>
          <DialogDescription>
            Select a proven storytelling framework to guide your content adaptation. Each framework will be added to your custom instructions.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {frameworks.map((framework) => (
            <Card key={framework.acronym} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{framework.name}</CardTitle>
                    <Badge variant="secondary" className="mt-1">{framework.acronym}</Badge>
                  </div>
                </div>
                <CardDescription className="mt-2">
                  {framework.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Structure:</p>
                  <p className="text-sm">{framework.structure}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Best for:</p>
                  <p className="text-sm">{framework.bestFor}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Example:</p>
                  <p className="text-sm italic text-muted-foreground">{framework.example}</p>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => handleSelect(framework)}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Use This Framework
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
